import { HandlerEvent } from 'post-robot';
import { ValidationError, error } from 'common/errors';
import {
  pushAuthenticateGenerateFlowFinished,
  pushAuthenticateGenerateFlowInitiated,
  pushAuthenticateValidateFlowFinished,
  pushAuthenticateValidateFlowInitiated,
  pushErrors
} from 'common/gamp';
import { trackError, trackEvent } from 'common/observability';
import { reset } from 'communicator/store';
import {
  getAccessToken,
  getApiKey,
  getAuthenticateRequestPayload,
  getIsFeatureToggleEnabled
} from 'communicator/store/selectors';
import {
  FetchAuthenticateOptions,
  fetchAuthenticate,
  fetchAuthenticateResults
} from 'communicator/utils/api/authenticate';
import { getAuthenticateErrorMapping } from 'communicator/utils/authenticate-error';
import { markEnd, markStart } from 'communicator/utils/instrumentation';
import { AuthenticateCommunicatorPayload, AuthenticateRequest } from 'types/visa-sdk/authenticate';

export default async function authenticate(
  event: HandlerEvent<AuthenticateRequest>
): Promise<AuthenticateCommunicatorPayload | ValidationError> {
  try {
    if (!getIsFeatureToggleEnabled('enableIssuerAppAuth')) {
      return error('Issuer app authentication is not supported yet.');
    }

    if (!getIsFeatureToggleEnabled('enabledIntegratedIssuerFlow')) {
      return error('Current version of SDK does not support authenticate method');
    }

    const validateOtpInstrumentationKey = [
      'AUTHENTICATE_OTP_VALIDATE',
      'authenticate_api_validate_otp'
    ];

    const generateOtpInstrumentationKey = [
      'AUTHENTICATE_OTP_GENERATE',
      'authenticate_api_generate_otp'
    ];

    const accessToken = getAccessToken()?.token as string;
    const authenticateData = event.data.data;
    const isOtpValuePresent =
      authenticateData.authenticationMethod.methodAttributes?.otpValue !== undefined;

    const [sdkInstrumentationKey, apiInstrumentationKey] = isOtpValuePresent
      ? validateOtpInstrumentationKey
      : generateOtpInstrumentationKey;

    const [pushGampAuthenticateFlowInitiated, pushGampAuthenticateFlowFinished] = isOtpValuePresent
      ? [pushAuthenticateValidateFlowInitiated, pushAuthenticateValidateFlowFinished]
      : [pushAuthenticateGenerateFlowInitiated, pushAuthenticateGenerateFlowFinished];

    const stime = Date.now();
    markStart(sdkInstrumentationKey);
    pushGampAuthenticateFlowInitiated();

    const authReqPayload: FetchAuthenticateOptions = {
      accessToken,
      apikey: getApiKey() as string,
      requestPayload: getAuthenticateRequestPayload(authenticateData)
    };

    if (event.data.fetchAuthenticationResults) {
      const polledAuthenticationResults = await fetchAuthenticateResults(authReqPayload);

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!polledAuthenticationResults?.ok) {
        // START GENAI@GHCOPILOT
        const msg = 'Authentication has failed. Please choose another method to authenticate.';
        markEnd(sdkInstrumentationKey, {
          doFlush: true,
          failure: true,
          reason: msg
        });

        pushErrors('AUTH_ERROR', msg);

        // START GENAI@GHCOPILOT
        trackError('authenticate:failure', {
          reason: msg
        });
        // END GENAI@GHCOPILOT

        return error('AUTH_ERROR', msg);
        // END GENAI@GHCOPILOT
      }

      const { authenticationSessionId, ...authenticateCommunicatorResponse } =
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        polledAuthenticationResults?.data || {};

      markEnd(sdkInstrumentationKey, { doFlush: true });
      pushGampAuthenticateFlowFinished(Date.now() - stime);

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!polledAuthenticationResults?.data) {
        // START GENAI@GHCOPILOT
        const msg = 'Authentication has failed. Please choose another method to authenticate.';
        pushErrors('AUTH_ERROR', msg);

        // START GENAI@GHCOPILOT
        trackError('authenticate:failure', {
          reason: msg
        });
        // END GENAI@GHCOPILOT

        return error('AUTH_ERROR', msg);
        // END GENAI@GHCOPILOT
      }

      return {
        payload: authenticateCommunicatorResponse
      };
    }

    markStart(apiInstrumentationKey);
    const authenticateResponse = await fetchAuthenticate(authReqPayload);

    markEnd(apiInstrumentationKey, {
      failure: !authenticateResponse.ok,
      reason: authenticateResponse.ok ? undefined : 'AUTHENTICATE_FAILURE'
    });

    if (!authenticateResponse.ok) {
      const { errorCode, errorMessage } = await getAuthenticateErrorMapping(authenticateResponse);

      if (errorCode === 'RETRIES_EXCEEDED') {
        // START GENAI@GHCOPILOT
        pushErrors('RETRIES_EXCEEDED', errorCode);
        // END GENAI@GHCOPILOT
        // START GENAI@GHCOPILOT
        trackError('authenticate:failure', {
          reason: errorCode,
          errorMessage
        });

        // END GENAI@GHCOPILOT
        reset();
      }

      markEnd(sdkInstrumentationKey, {
        doFlush: true,
        failure: true,
        reason: errorCode
      });

      // START GENAI@GHCOPILOT
      pushErrors('AUTH_FAILURE', errorCode);
      // END GENAI@GHCOPILOT

      // START GENAI@GHCOPILOT
      trackError('authenticate:failure', {
        reason: errorCode,
        errorMessage
      });
      // END GENAI@GHCOPILOT

      return error(errorCode, errorMessage);
    }

    const { authenticationSessionId, ...authenticateCommunicatorResponse } =
      authenticateResponse.data;

    markEnd(sdkInstrumentationKey, { doFlush: true });
    pushGampAuthenticateFlowFinished(Date.now() - stime);

    // START GENAI@GHCOPILOT
    trackEvent('authenticate:success', {
      authenticationSessionId,
      isOtpValuePresent
    });
    // END GENAI@GHCOPILOT

    return {
      payload: authenticateCommunicatorResponse
    };
  } catch (err: unknown) {
    // START GENAI@GHCOPILOT
    pushErrors('AUTH_FAILURE', err);
    // START GENAI@GHCOPILOT
    trackError('authenticate:failure', {
      reason: 'AUTHENTICATE_FAILURE',
      error: err
    });

    // END GENAI@GHCOPILOT
    return error('AUTHENTICATE_FAILURE', 'Authentication has been failed.');
    // END GENAI@GHCOPILOT
  }
}
